import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Announcement } from '@breathelife/types';

export type AnnouncementsState = {
  announcements: Announcement[];
  selectedAnnouncement?: Announcement;
  isFetching: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isReordering: boolean;
};

type SetAnnouncementsPayload = { announcements: Announcement[] };
type SetAnnouncementPayload = { announcement: Announcement };
type SetLoadingPayload = boolean;

type ActionsType = {
  setAnnouncements: (state: AnnouncementsState, { payload }: PayloadAction<SetAnnouncementsPayload>) => void;
  setSelectedAnnouncement: (
    state: AnnouncementsState,
    { payload }: PayloadAction<SetAnnouncementPayload | undefined>,
  ) => void;
  addAnnouncement: (state: AnnouncementsState, { payload }: PayloadAction<SetAnnouncementPayload>) => void;
  updateAnnouncement: (state: AnnouncementsState, { payload }: PayloadAction<SetAnnouncementPayload>) => void;
  removeAnnouncement: (state: AnnouncementsState, { payload }: PayloadAction<{ id: string }>) => void;
  setIsFetching: (state: AnnouncementsState, { payload }: PayloadAction<SetLoadingPayload>) => void;
  setIsCreating: (state: AnnouncementsState, { payload }: PayloadAction<SetLoadingPayload>) => void;
  setIsUpdating: (state: AnnouncementsState, { payload }: PayloadAction<SetLoadingPayload>) => void;
  setIsDeleting: (state: AnnouncementsState, { payload }: PayloadAction<SetLoadingPayload>) => void;
  setIsReordering: (state: AnnouncementsState, { payload }: PayloadAction<SetLoadingPayload>) => void;
};

const defaultState: AnnouncementsState = {
  announcements: [],
  selectedAnnouncement: undefined,
  isFetching: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isReordering: false,
};

export const announcementsSlice = createSlice<AnnouncementsState, ActionsType>({
  name: 'leadPlatform/announcements',
  initialState: defaultState,
  reducers: {
    setAnnouncements: (state: AnnouncementsState, { payload }: PayloadAction<SetAnnouncementsPayload>) => {
      state.announcements = payload.announcements;
    },
    setSelectedAnnouncement: (
      state: AnnouncementsState,
      { payload }: PayloadAction<SetAnnouncementPayload | undefined>,
    ) => {
      state.selectedAnnouncement = payload?.announcement;
    },
    addAnnouncement: (state: AnnouncementsState, { payload }: PayloadAction<SetAnnouncementPayload>) => {
      state.announcements.push(payload.announcement);
    },
    updateAnnouncement: (state: AnnouncementsState, { payload }: PayloadAction<SetAnnouncementPayload>) => {
      const index = state.announcements.findIndex((a) => a.id === payload.announcement.id);
      if (index !== -1) {
        state.announcements[index] = payload.announcement;
      }
      if (state.selectedAnnouncement?.id === payload.announcement.id) {
        state.selectedAnnouncement = payload.announcement;
      }
    },
    removeAnnouncement: (state: AnnouncementsState, { payload }: PayloadAction<{ id: string }>) => {
      state.announcements = state.announcements.filter((a) => a.id !== payload.id);
      if (state.selectedAnnouncement?.id === payload.id) {
        state.selectedAnnouncement = undefined;
      }
    },
    setIsFetching: (state: AnnouncementsState, { payload }: PayloadAction<SetLoadingPayload>) => {
      state.isFetching = payload;
    },
    setIsCreating: (state: AnnouncementsState, { payload }: PayloadAction<SetLoadingPayload>) => {
      state.isCreating = payload;
    },
    setIsUpdating: (state: AnnouncementsState, { payload }: PayloadAction<SetLoadingPayload>) => {
      state.isUpdating = payload;
    },
    setIsDeleting: (state: AnnouncementsState, { payload }: PayloadAction<SetLoadingPayload>) => {
      state.isDeleting = payload;
    },
    setIsReordering: (state: AnnouncementsState, { payload }: PayloadAction<SetLoadingPayload>) => {
      state.isReordering = payload;
    },
  },
});
